import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Badge,
  CircularProgress,
  Snackbar,
  InputAdornment,
  Menu,
  Checkbox,
  FormGroup,
  Collapse,
  Skeleton,
  TableSortLabel,
  OutlinedInput,
  InputLabel as MuiInputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  Rule as RuleIcon,
  Analytics as AnalyticsIcon,
  Block as BlockIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircle,
  CloudUpload,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ViewColumn as ViewColumnIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Sort as SortIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { suppressionListsAPI, accountsAPI, bulkImportAPI } from '../../../services/leadGenAPI';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog';

const SuppressionLists = () => {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [filteredLists, setFilteredLists] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [ruleBuilderOpen, setRuleBuilderOpen] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [activeTab, setActiveTab] = useState('lists'); // 'lists', 'rules', 'analytics'
  
  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Success/Error messages
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Account and Campaign selection state
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  // Enhanced table features
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [listTypeFilter, setListTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({
    name: true,
    scope: true,
    rules: true,
    campaigns: true,
    usage: true,
    suppressionRate: true,
    status: true,
    lastUpdated: true,
    actions: true,
  });
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);

  // Available columns for visibility control
  const availableColumns = [
    { key: 'name', label: 'List Name & Type' },
    { key: 'scope', label: 'Scope' },
    { key: 'rules', label: 'Rules' },
    { key: 'campaigns', label: 'Campaigns' },
    { key: 'usage', label: 'Usage' },
    { key: 'suppressionRate', label: 'Suppression Rate' },
    { key: 'status', label: 'Status' },
    { key: 'lastUpdated', label: 'Last Updated' },
    { key: 'actions', label: 'Actions' },
  ];

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      fetchCampaigns(selectedAccount);
    } else {
      setCampaigns([]);
      setSelectedCampaign('');
    }
  }, [selectedAccount]);

  useEffect(() => {
    if (selectedAccount) {
      fetchSuppressionLists();
    }
  }, [selectedAccount, selectedCampaign]);

  // Apply filters and search when data changes
  useEffect(() => {
    applyFiltersAndSearch();
  }, [lists, searchTerm, statusFilter, typeFilter, listTypeFilter, sortBy, sortOrder]);

  const applyFiltersAndSearch = () => {
    let filtered = [...lists];

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(list =>
        list.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        list.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        list.suppression_type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(list => list.status === statusFilter);
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(list => list.suppression_type === typeFilter);
    }

    // Apply list type filter
    if (listTypeFilter !== 'all') {
      filtered = filtered.filter(list => list.list_type === listTypeFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle nested properties
      if (sortBy === 'name') {
        aValue = a.name || '';
        bValue = b.name || '';
      } else if (sortBy === 'rules') {
        aValue = a.total_rules_count || 0;
        bValue = b.total_rules_count || 0;
      } else if (sortBy === 'usage') {
        aValue = a.usage_count || 0;
        bValue = b.usage_count || 0;
      } else if (sortBy === 'suppressionRate') {
        aValue = a.suppression_rate || 0;
        bValue = b.suppression_rate || 0;
      } else if (sortBy === 'lastUpdated') {
        aValue = new Date(a.updated_at || 0);
        bValue = new Date(b.updated_at || 0);
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredLists(filtered);
    setPage(0); // Reset to first page when filters change
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleColumnVisibilityChange = (column) => {
    setColumnVisibility(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const handleExportData = () => {
    const csvContent = [
      // Header
      ['Name', 'Type', 'List Type', 'Scope', 'Rules Count', 'Usage Count', 'Suppression Rate', 'Status', 'Last Updated'].join(','),
      // Data
      ...filteredLists.map(list => [
        list.name,
        list.suppression_type,
        list.list_type,
        list.is_global ? 'Global' : 'Account',
        list.total_rules_count || 0,
        list.usage_count || 0,
        list.suppression_rate || 0,
        list.status,
        formatDate(list.updated_at)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `suppression-lists-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setListTypeFilter('all');
    setSortBy('name');
    setSortOrder('asc');
  };

  const getUniqueTypes = () => {
    const types = [...new Set(lists.map(list => list.suppression_type).filter(Boolean))];
    return types;
  };

  const getUniqueListTypes = () => {
    const types = [...new Set(lists.map(list => list.list_type).filter(Boolean))];
    return types;
  };

  const fetchAccounts = async () => {
    setAccountsLoading(true);
    try {
      const response = await accountsAPI.getAccounts();
      setAccounts(response);
      // Set the first account as default if available
      if (response && response.length > 0) {
        setSelectedAccount(response[0].id);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  const fetchCampaigns = async (accountId) => {
    setCampaignsLoading(true);
    try {
      const response = await bulkImportAPI.getAccountCampaigns(accountId);
      setCampaigns(response);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const fetchSuppressionLists = async () => {
    if (!selectedAccount) {
      setLists([]);
      return;
    }

    setLoading(true);
    try {
      const params = { account_id: selectedAccount };
      if (selectedCampaign) {
        params.campaign_id = selectedCampaign;
      }
      const response = await suppressionListsAPI.getLists(params);
      setLists(response.results || response);
    } catch (error) {
      console.error('Error fetching suppression lists:', error);
      setLists([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountChange = (event) => {
    setSelectedAccount(event.target.value);
    setSelectedCampaign(''); // Reset campaign selection when account changes
  };

  const handleCampaignChange = (event) => {
    setSelectedCampaign(event.target.value);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleStatusChange = async (listId, newStatus) => {
    try {
      await suppressionListsAPI.updateList(listId, { status: newStatus });
      setLists(prev => prev.map(list =>
        list.id === listId ? { ...list, status: newStatus } : list
      ));
      showSnackbar(`List status updated to ${newStatus}`, 'success');
    } catch (error) {
      console.error('Error updating list status:', error);
      showSnackbar(`Failed to update list status: ${error.message}`, 'error');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'contact': return <PersonIcon />;
      case 'company': return <BusinessIcon />;
      case 'geographic': return <LocationIcon />;
      case 'demographic': return <PersonIcon />;
      default: return <BlockIcon />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'contact': return 'primary';
      case 'company': return 'secondary';
      case 'geographic': return 'info';
      case 'demographic': return 'warning';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDeleteClick = (list) => {
    setListToDelete(list);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!listToDelete) return;

    setDeleteLoading(true);
    try {
      await suppressionListsAPI.deleteList(listToDelete.id);
      setLists(prev => prev.filter(list => list.id !== listToDelete.id));
      showSnackbar(`Suppression list "${listToDelete.name}" deleted successfully`, 'success');
      setDeleteDialogOpen(false);
      setListToDelete(null);
    } catch (error) {
      console.error('Error deleting suppression list:', error);
      showSnackbar(`Failed to delete suppression list: ${error.message}`, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setListToDelete(null);
  };

  const handleDuplicateList = async (list) => {
    try {
      const duplicatedList = await suppressionListsAPI.duplicateList(list.id);
      setLists(prev => [...prev, duplicatedList]);
      showSnackbar(`Suppression list "${list.name}" duplicated successfully`, 'success');
    } catch (error) {
      console.error('Error duplicating suppression list:', error);
      showSnackbar(`Failed to duplicate suppression list: ${error.message}`, 'error');
    }
  };

  const renderListsTab = () => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" color="text.primary">
          Suppression Lists
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() => navigate('/lead-gen/suppression/bulk-upload')}
          >
            Bulk Upload
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/lead-gen/suppression/create')}
          >
            Create List
          </Button>
        </Box>
      </Box>

      {/* Enhanced Search and Filter Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search lists by name, description, or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm('')}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Filter Toggle */}
            <Grid item xs={12} md={2}>
              <Button
                variant={showFilters ? "contained" : "outlined"}
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
                fullWidth
              >
                Filters {showFilters ? `(${[
                  statusFilter !== 'all' ? 1 : 0,
                  typeFilter !== 'all' ? 1 : 0,
                  listTypeFilter !== 'all' ? 1 : 0
                ].reduce((a, b) => a + b, 0)})` : ''}
              </Button>
            </Grid>

            {/* Column Visibility */}
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
                fullWidth
              >
                Columns
              </Button>
            </Grid>

            {/* Export */}
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportData}
                disabled={filteredLists.length === 0}
                fullWidth
              >
                Export
              </Button>
            </Grid>

            {/* Clear Filters */}
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearAllFilters}
                disabled={!searchTerm && statusFilter === 'all' && typeFilter === 'all' && listTypeFilter === 'all'}
                fullWidth
              >
                Clear All
              </Button>
            </Grid>
          </Grid>

          {/* Expandable Filters */}
          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      {getUniqueTypes().map(type => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>List Type</InputLabel>
                    <Select
                      value={listTypeFilter}
                      onChange={(e) => setListTypeFilter(e.target.value)}
                      label="List Type"
                    >
                      <MenuItem value="all">All List Types</MenuItem>
                      {getUniqueListTypes().map(type => (
                        <MenuItem key={type} value={type}>
                          {type === 'whitelist' ? 'Whitelist' : 'Blacklist'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box display="flex" alignItems="center" height="100%">
                    <Typography variant="body2" color="textSecondary">
                      Showing {filteredLists.length} of {lists.length} lists
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Account and Campaign Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" color="text.primary" gutterBottom>
            Filter Options
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Account</InputLabel>
                <Select
                  value={selectedAccount}
                  onChange={handleAccountChange}
                  label="Select Account"
                  disabled={accountsLoading}
                >
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Campaign (Optional)</InputLabel>
                <Select
                  value={selectedCampaign}
                  onChange={handleCampaignChange}
                  label="Select Campaign (Optional)"
                  disabled={campaignsLoading || !selectedAccount}
                >
                  <MenuItem value="">
                    <em>All Campaigns</em>
                  </MenuItem>
                  {campaigns.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Performance Overview */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Lists
              </Typography>
              <Typography variant="h4" component="div">
                {selectedAccount ? lists.length : '—'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {selectedAccount ? `${lists.filter(l => l.status === 'active').length} active` : 'Select account'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Rules
              </Typography>
              <Typography variant="h4" component="div">
                {selectedAccount ? lists.reduce((sum, list) => sum + (list.total_rules_count || 0), 0) : '—'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {selectedAccount ? 'Across all lists' : 'Select account'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Avg Suppression Rate
              </Typography>
              <Typography variant="h4" component="div">
                {selectedAccount && lists.length > 0 
                  ? `${(lists.reduce((sum, list) => sum + (list.suppression_rate || 0), 0) / lists.length).toFixed(1)}%`
                  : '—'
                }
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {selectedAccount ? 'Across all lists' : 'Select account'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Suppressions
              </Typography>
              <Typography variant="h4" component="div">
                {selectedAccount ? lists.reduce((sum, list) => sum + (list.usage_count || 0), 0).toLocaleString() : '—'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {selectedAccount ? 'This month' : 'Select account'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Lists Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columnVisibility.name && (
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortOrder : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      List Name & Type
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.scope && (
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'scope'}
                      direction={sortBy === 'scope' ? sortOrder : 'asc'}
                      onClick={() => handleSort('scope')}
                    >
                      Scope
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.rules && (
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'rules'}
                      direction={sortBy === 'rules' ? sortOrder : 'asc'}
                      onClick={() => handleSort('rules')}
                    >
                      Rules
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.campaigns && (
                  <TableCell>Campaigns</TableCell>
                )}
                {columnVisibility.usage && (
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'usage'}
                      direction={sortBy === 'usage' ? sortOrder : 'asc'}
                      onClick={() => handleSort('usage')}
                    >
                      Usage
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.suppressionRate && (
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'suppressionRate'}
                      direction={sortBy === 'suppressionRate' ? sortOrder : 'asc'}
                      onClick={() => handleSort('suppressionRate')}
                    >
                      Suppression Rate
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.status && (
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortOrder : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.lastUpdated && (
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'lastUpdated'}
                      direction={sortBy === 'lastUpdated' ? sortOrder : 'asc'}
                      onClick={() => handleSort('lastUpdated')}
                    >
                      Last Updated
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.actions && (
                  <TableCell>Actions</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Loading skeleton rows
                Array.from({ length: rowsPerPage }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {columnVisibility.name && (
                      <TableCell>
                        <Box>
                          <Skeleton variant="text" width="60%" />
                          <Skeleton variant="text" width="40%" />
                          <Skeleton variant="text" width="80%" />
                        </Box>
                      </TableCell>
                    )}
                    {columnVisibility.scope && (
                      <TableCell>
                        <Skeleton variant="rectangular" width={60} height={24} />
                      </TableCell>
                    )}
                    {columnVisibility.rules && (
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Skeleton variant="circular" width={20} height={20} />
                          <Skeleton variant="text" width={40} />
                        </Box>
                      </TableCell>
                    )}
                    {columnVisibility.campaigns && (
                      <TableCell>
                        <Skeleton variant="rectangular" width={80} height={24} />
                      </TableCell>
                    )}
                    {columnVisibility.usage && (
                      <TableCell>
                        <Skeleton variant="text" width={50} />
                      </TableCell>
                    )}
                    {columnVisibility.suppressionRate && (
                      <TableCell>
                        <Skeleton variant="text" width={40} />
                      </TableCell>
                    )}
                    {columnVisibility.status && (
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Skeleton variant="rectangular" width={60} height={24} />
                          <Skeleton variant="rectangular" width={30} height={20} />
                        </Box>
                      </TableCell>
                    )}
                    {columnVisibility.lastUpdated && (
                      <TableCell>
                        <Skeleton variant="text" width={80} />
                      </TableCell>
                    )}
                    {columnVisibility.actions && (
                      <TableCell>
                        <Box display="flex" gap={1}>
                          {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} variant="circular" width={32} height={32} />
                          ))}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : !selectedAccount ? (
                <TableRow>
                  <TableCell colSpan={Object.values(columnVisibility).filter(Boolean).length} align="center">
                    <Typography variant="body2" color="textSecondary">
                      Please select an account to view suppression lists
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredLists.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={Object.values(columnVisibility).filter(Boolean).length} align="center">
                    <Box py={3}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || listTypeFilter !== 'all'
                          ? 'No suppression lists match your current filters'
                          : 'No suppression lists found for the selected account'
                        }
                      </Typography>
                      {(searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || listTypeFilter !== 'all') && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={clearAllFilters}
                          startIcon={<ClearIcon />}
                        >
                          Clear Filters
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLists
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((list) => (
                    <TableRow key={list.id} hover>
                      {columnVisibility.name && (
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2">{list.name}</Typography>
                            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                              {getTypeIcon(list.suppression_type || 'contact')}
                              <Chip 
                                label={list.suppression_type_display || list.suppression_type || 'contact'} 
                                size="small" 
                                color={getTypeColor(list.suppression_type || 'contact')}
                                variant="outlined"
                              />
                              <Chip 
                                label={list.list_type === 'whitelist' ? 'Whitelist' : 'Blacklist'} 
                                size="small" 
                                color={list.list_type === 'whitelist' ? 'success' : 'error'}
                                variant="outlined"
                              />
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                              {list.description || 'No description'}
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {columnVisibility.scope && (
                        <TableCell>
                          <Chip 
                            label={list.is_global ? 'Global' : 'Account'} 
                            size="small"
                            color={list.is_global ? 'primary' : 'secondary'}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.rules && (
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Badge badgeContent={list.total_rules_count || 0} color="primary">
                              <RuleIcon />
                            </Badge>
                            <Typography variant="body2">
                              {list.total_rules_count || 0} rules
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      {columnVisibility.campaigns && (
                        <TableCell>
                          <Box>
                            {list.campaign_names && list.campaign_names.length > 0 ? (
                              <Box display="flex" flexDirection="column" gap={0.5}>
                                {list.campaign_names.slice(0, 2).map((campaignName, index) => (
                                  <Chip
                                    key={index}
                                    label={campaignName}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                  />
                                ))}
                                {list.campaign_names.length > 2 && (
                                  <Typography variant="caption" color="textSecondary">
                                    +{list.campaign_names.length - 2} more
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No campaigns
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      )}
                      {columnVisibility.usage && (
                        <TableCell>
                          <Typography variant="body2">
                            {(list.usage_count || 0).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            this month
                          </Typography>
                        </TableCell>
                      )}
                      {columnVisibility.suppressionRate && (
                        <TableCell>
                          <Typography variant="body2" color="success.main">
                            {list.suppression_rate || 0}%
                          </Typography>
                        </TableCell>
                      )}
                      {columnVisibility.status && (
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={list.status || 'inactive'}
                              color={getStatusColor(list.status || 'inactive')}
                              size="small"
                            />
                            <Switch
                              size="small"
                              checked={(list.status || 'inactive') === 'active'}
                              onChange={(e) => handleStatusChange(list.id, e.target.checked ? 'active' : 'inactive')}
                            />
                          </Box>
                        </TableCell>
                      )}
                      {columnVisibility.lastUpdated && (
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {formatDate(list.updated_at)}
                          </Typography>
                        </TableCell>
                      )}
                      {columnVisibility.actions && (
                        <TableCell>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/lead-gen/suppression/${list.id}`)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Rules">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedList(list);
                                  setRuleBuilderOpen(true);
                                }}
                              >
                                <RuleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Analytics">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/lead-gen/suppression/${list.id}/analytics`)}
                              >
                                <AnalyticsIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit List">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/lead-gen/suppression/${list.id}/edit`)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Duplicate List">
                              <IconButton
                                size="small"
                                onClick={() => handleDuplicateList(list)}
                              >
                                <AddCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete List">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(list)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100, 250]}
          component="div"
          count={filteredLists.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
          labelRowsPerPage="Rows per page:"
        />
      </Paper>

      {/* Column Visibility Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={() => setColumnMenuAnchor(null)}
        PaperProps={{
          sx: { minWidth: 200 }
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" color="text.primary">
            Visible Columns
          </Typography>
        </Box>
        <FormGroup sx={{ p: 1 }}>
          {availableColumns.map((column) => (
            <FormControlLabel
              key={column.key}
              control={
                <Checkbox
                  checked={columnVisibility[column.key]}
                  onChange={() => handleColumnVisibilityChange(column.key)}
                  size="small"
                />
              }
              label={column.label}
              sx={{ 
                mx: 0,
                '& .MuiFormControlLabel-label': { fontSize: '0.875rem' }
              }}
            />
          ))}
        </FormGroup>
      </Menu>
    </>
  );

  const renderRuleBuilder = () => (
    <Dialog 
      open={ruleBuilderOpen} 
      onClose={() => setRuleBuilderOpen(false)}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        Rule Builder - {selectedList?.name}
      </DialogTitle>
      <DialogContent>
        {selectedList && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="text.primary">
                      Current Rules
                    </Typography>
                    <List>
                      {selectedList.rules.map((rule, index) => (
                        <React.Fragment key={rule.id}>
                          <ListItem>
                            <ListItemIcon>
                              <RuleIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${rule.field} ${rule.operator} ${rule.value}`}
                              secondary={`Rule ${index + 1}`}
                            />
                            <IconButton size="small" color="error">
                              <DeleteIcon />
                            </IconButton>
                          </ListItem>
                          {index < selectedList.rules.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                    
                    <Button
                      startIcon={<AddCircleIcon />}
                      variant="outlined"
                      sx={{ mt: 2 }}
                    >
                      Add New Rule
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="text.primary">
                      Rule Builder
                    </Typography>
                    <Typography variant="body2" color="textSecondary" mb={2}>
                      Create new suppression rules using the visual builder.
                    </Typography>
                    
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Field</InputLabel>
                      <Select label="Field">
                        <MenuItem value="email">Email</MenuItem>
                        <MenuItem value="phone">Phone</MenuItem>
                        <MenuItem value="company_name">Company Name</MenuItem>
                        <MenuItem value="company_domain">Company Domain</MenuItem>
                        <MenuItem value="state">State</MenuItem>
                        <MenuItem value="country">Country</MenuItem>
                        <MenuItem value="job_title">Job Title</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Operator</InputLabel>
                      <Select label="Operator">
                        <MenuItem value="equals">Equals</MenuItem>
                        <MenuItem value="not_equals">Not Equals</MenuItem>
                        <MenuItem value="contains">Contains</MenuItem>
                        <MenuItem value="not_contains">Not Contains</MenuItem>
                        <MenuItem value="starts_with">Starts With</MenuItem>
                        <MenuItem value="ends_with">Ends With</MenuItem>
                        <MenuItem value="in">In</MenuItem>
                        <MenuItem value="not_in">Not In</MenuItem>
                        <MenuItem value="is_empty">Is Empty</MenuItem>
                        <MenuItem value="is_not_empty">Is Not Empty</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <TextField
                      fullWidth
                      label="Value"
                      placeholder="Enter value..."
                      sx={{ mb: 2 }}
                    />
                    
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<AddCircleIcon />}
                    >
                      Add Rule
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRuleBuilderOpen(false)}>
          Close
        </Button>
        <Button variant="contained">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Tab Navigation */}
      <Box display="flex" gap={1} mb={3}>
        <Button
          variant={activeTab === 'lists' ? 'contained' : 'outlined'}
          onClick={() => setActiveTab('lists')}
        >
          Lists
        </Button>
        <Button
          variant={activeTab === 'analytics' ? 'contained' : 'outlined'}
          onClick={() => setActiveTab('analytics')}
        >
          Analytics
        </Button>
      </Box>

      {activeTab === 'lists' && renderListsTab()}
      {activeTab === 'analytics' && (
        <Box>
          <Typography variant="h4" component="h1" mb={3} color="text.primary">
            Suppression Analytics
          </Typography>
          <Alert severity="info">
            Analytics dashboard coming soon. This will show suppression rate trends, 
            geographic patterns, and campaign-specific suppression rates.
          </Alert>
        </Box>
      )}

      {renderRuleBuilder()}

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Suppression List"
        message={`Are you sure you want to delete the suppression list "${listToDelete?.name}"? This action cannot be undone. All rules and data associated with this list will be permanently removed.`}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SuppressionLists; 